import { useCallback, useEffect, useState } from "react";
import { X, Plus, Trash, CurrencyEur, CheckCircle, Warning, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { SortHeader } from "./SortHeader";
import { useTableSort } from "../lib/useTableSort";

const fmtEUR = (n) => `${Number(n || 0).toFixed(2)} €`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};

export default function AdvancesDialog({ worker, onClose }) {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // {advance_id: bool}
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ reason: "", amount: "", discount_enabled: true, date: new Date().toISOString().slice(0, 10) });
  const [payForm, setPayForm] = useState({}); // {advance_id: {amount, date, notes}}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${worker.id}/advances`);
      setAdvances(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [worker.id]);

  useEffect(() => { load(); }, [load]);

  const totals = advances.reduce(
    (acc, a) => {
      acc.total += a.amount;
      acc.paid += a.total_paid;
      acc.pending += a.pending;
      if (a.settled) acc.settled += 1;
      return acc;
    },
    { total: 0, paid: 0, pending: 0, settled: 0 }
  );

  const createAdvance = async (e) => {
    e.preventDefault();
    if (!newForm.reason.trim()) { toast.error("Indica un motivo"); return; }
    const amt = parseFloat(newForm.amount);
    if (!(amt > 0)) { toast.error("Importe inválido"); return; }
    setBusy(true);
    try {
      await api.post(`/users/${worker.id}/advances`, {
        reason: newForm.reason.trim(),
        amount: amt,
        discount_enabled: !!newForm.discount_enabled,
        date: newForm.date || undefined,
      });
      toast.success("Adelanto creado");
      setShowNew(false);
      setNewForm({ reason: "", amount: "", discount_enabled: true, date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addPayment = async (advanceId) => {
    const f = payForm[advanceId] || {};
    const amt = parseFloat(f.amount);
    if (!(amt > 0)) { toast.error("Importe inválido"); return; }
    setBusy(true);
    try {
      await api.post(`/advances/${advanceId}/payments`, {
        amount: amt,
        date: f.date || undefined,
        notes: (f.notes || "").trim(),
      });
      toast.success("Pago registrado");
      setPayForm((s) => ({ ...s, [advanceId]: { amount: "", date: new Date().toISOString().slice(0, 10), notes: "" } }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleDiscount = async (a) => {
    setBusy(true);
    try {
      await api.patch(`/advances/${a.id}`, { discount_enabled: !a.discount_enabled });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeAdvance = async (a) => {
    if (!window.confirm(`¿Eliminar el adelanto de ${fmtEUR(a.amount)} — "${a.reason}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/advances/${a.id}`);
      toast.success("Adelanto eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removePayment = async (advanceId, paymentId) => {
    if (!window.confirm("¿Eliminar este pago?")) return;
    setBusy(true);
    try {
      await api.delete(`/advances/${advanceId}/payments/${paymentId}`);
      toast.success("Pago eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="advances-dialog"
      >
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <CurrencyEur size={24} weight="duotone" className="text-[var(--primary)]" />
            <div>
              <div className="label-cap">adelantos</div>
              <h3 className="font-display font-bold text-xl">{worker.name}</h3>
            </div>
          </div>
          <button onClick={onClose} data-testid="advances-close" className="p-2 rounded hover:bg-[var(--bg)]">
            <X size={20} />
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 py-4 border-b border-[var(--border)] bg-[var(--bg)]">
          <Stat label="Total adelantado" value={fmtEUR(totals.total)} testId="advances-total" />
          <Stat label="Pagado" value={fmtEUR(totals.paid)} testId="advances-paid" tone="text-emerald-700" />
          <Stat label="Pendiente" value={fmtEUR(totals.pending)} testId="advances-pending" tone="text-amber-700" />
          <Stat label="Saldados" value={`${totals.settled} / ${advances.length}`} testId="advances-settled" />
        </div>

        {/* Action bar */}
        <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-[var(--border)]">
          <div className="text-xs text-[var(--text-secondary)]">
            {advances.length === 0 ? "Sin adelantos registrados." : `${advances.length} adelanto${advances.length === 1 ? "" : "s"} en total.`}
          </div>
          <button
            onClick={() => setShowNew((v) => !v)}
            data-testid="advances-add-toggle"
            className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-xl font-display font-bold text-sm"
          >
            <Plus size={16} weight="bold" /> {showNew ? "Cancelar" : "Nuevo adelanto"}
          </button>
        </div>

        {/* New advance form */}
        {showNew && (
          <form onSubmit={createAdvance} className="px-5 py-4 border-b border-[var(--border)] bg-[var(--bg)] grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="advances-new-form">
            <Field label="Motivo" span={2}>
              <input
                required
                value={newForm.reason}
                onChange={(e) => setNewForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="Ej. Gastos escolares"
                data-testid="advances-new-reason"
                className="form-input"
              />
            </Field>
            <Field label="Importe (€)">
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                value={newForm.amount}
                onChange={(e) => setNewForm((f) => ({ ...f, amount: e.target.value }))}
                data-testid="advances-new-amount"
                className="form-input"
              />
            </Field>
            <Field label="Fecha">
              <input
                type="date"
                value={newForm.date}
                onChange={(e) => setNewForm((f) => ({ ...f, date: e.target.value }))}
                data-testid="advances-new-date"
                className="form-input"
              />
            </Field>
            <label className="md:col-span-3 flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] bg-white cursor-pointer">
              <input
                type="checkbox"
                checked={!!newForm.discount_enabled}
                onChange={(e) => setNewForm((f) => ({ ...f, discount_enabled: e.target.checked }))}
                data-testid="advances-new-discount"
                className="w-5 h-5 accent-[var(--primary)]"
              />
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">Descontar del pago</div>
                <div className="text-xs text-[var(--text-secondary)]">Si está activo, el saldo pendiente se resta del coste calculado en el informe del trabajador.</div>
              </div>
            </label>
            <div className="md:col-span-1 flex items-end">
              <button
                type="submit"
                disabled={busy}
                data-testid="advances-new-submit"
                className="btn-primary w-full px-4 py-3 rounded-xl font-display font-bold disabled:opacity-50"
              >
                {busy ? "Guardando…" : "Crear adelanto"}
              </button>
            </div>
          </form>
        )}

        {/* Advances list */}
        <div className="flex-1 overflow-auto">
          {loading && <div className="p-8 text-center text-[var(--text-secondary)]">Cargando…</div>}
          {!loading && advances.length === 0 && (
            <div className="p-10 text-center text-[var(--text-secondary)]" data-testid="advances-empty">
              Este trabajador no tiene adelantos registrados. Pulsa <b>Nuevo adelanto</b> para crear el primero.
            </div>
          )}
          {!loading && advances.map((a) => {
            const isOpen = !!expanded[a.id];
            const pct = a.amount > 0 ? Math.min(100, Math.round((a.total_paid / a.amount) * 100)) : 0;
            return (
              <div key={a.id} className="border-b border-[var(--border)]" data-testid={`advance-row-${a.id}`}>
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <button
                      onClick={() => setExpanded((s) => ({ ...s, [a.id]: !isOpen }))}
                      data-testid={`advance-expand-${a.id}`}
                      className="inline-flex items-start gap-2 text-left flex-1 min-w-[220px]"
                    >
                      {isOpen ? <CaretDown size={18} weight="bold" className="mt-1" /> : <CaretRight size={18} weight="bold" className="mt-1" />}
                      <div>
                        <div className="font-display font-bold">{a.reason || "(sin motivo)"}</div>
                        <div className="text-xs text-[var(--text-secondary)]">
                          {fmtDate(a.date)} · {a.payments.length} pago{a.payments.length === 1 ? "" : "s"}
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">Importe</div>
                        <div className="font-mono font-bold">{fmtEUR(a.amount)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">Pagado</div>
                        <div className="font-mono font-bold text-emerald-700">{fmtEUR(a.total_paid)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">Pendiente</div>
                        <div className="font-mono font-bold text-amber-700">{fmtEUR(a.pending)}</div>
                      </div>
                      {a.settled ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-display font-bold bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`advance-settled-${a.id}`}>
                          <CheckCircle size={14} weight="fill" /> Saldado
                        </span>
                      ) : a.discount_enabled ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-display font-bold bg-amber-100 text-amber-800 border border-amber-200">
                          <Warning size={14} weight="fill" /> Descuenta
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-display font-bold bg-slate-100 text-slate-700 border border-slate-200">
                          Informativo
                        </span>
                      )}
                    </div>
                  </div>
                  {/* progress bar */}
                  <div className="mt-3 h-2 rounded-full bg-[var(--border)] overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                {isOpen && (
                  <div className="px-5 pb-5 bg-[var(--bg)] border-t border-[var(--border)]" data-testid={`advance-details-${a.id}`}>
                    {/* actions row */}
                    <div className="flex flex-wrap items-center gap-2 py-3">
                      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border)] bg-white cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={!!a.discount_enabled}
                          onChange={() => toggleDiscount(a)}
                          data-testid={`advance-toggle-${a.id}`}
                          className="w-4 h-4 accent-[var(--primary)]"
                        />
                        <span className="font-display font-semibold">Descontar del pago</span>
                      </label>
                      <button
                        onClick={() => removeAdvance(a)}
                        disabled={busy}
                        data-testid={`advance-delete-${a.id}`}
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--border)] hover:bg-red-50 hover:border-red-300 hover:text-red-700 font-display text-sm font-semibold"
                      >
                        <Trash size={14} weight="duotone" /> Eliminar adelanto
                      </button>
                    </div>

                    {/* payments list */}
                    <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden mb-3">
                      {a.payments.length === 0 ? (
                        <div className="p-4 text-center text-xs text-[var(--text-secondary)]" data-testid={`advance-no-payments-${a.id}`}>
                          Sin pagos registrados todavía.
                        </div>
                      ) : (
                        <PaymentsTable payments={a.payments} busy={busy} onRemove={(pid) => removePayment(a.id, pid)} />
                      )}
                    </div>

                    {/* add payment form */}
                    {!a.settled && (
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid={`advance-payment-form-${a.id}`}>
                        <Field label="Importe (€)">
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={a.pending}
                            value={payForm[a.id]?.amount || ""}
                            onChange={(e) => setPayForm((s) => ({ ...s, [a.id]: { ...(s[a.id] || {}), amount: e.target.value } }))}
                            data-testid={`payment-amount-${a.id}`}
                            className="form-input"
                            placeholder={`Máx ${a.pending.toFixed(2)}`}
                          />
                        </Field>
                        <Field label="Fecha">
                          <input
                            type="date"
                            value={payForm[a.id]?.date || new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setPayForm((s) => ({ ...s, [a.id]: { ...(s[a.id] || {}), date: e.target.value } }))}
                            data-testid={`payment-date-${a.id}`}
                            className="form-input"
                          />
                        </Field>
                        <Field label="Notas" span={3}>
                          <input
                            value={payForm[a.id]?.notes || ""}
                            onChange={(e) => setPayForm((s) => ({ ...s, [a.id]: { ...(s[a.id] || {}), notes: e.target.value } }))}
                            data-testid={`payment-notes-${a.id}`}
                            className="form-input"
                            placeholder="Opcional"
                          />
                        </Field>
                        <button
                          onClick={() => addPayment(a.id)}
                          disabled={busy}
                          data-testid={`payment-submit-${a.id}`}
                          className="btn-primary px-4 py-3 rounded-xl font-display font-bold text-sm disabled:opacity-50"
                        >
                          Añadir pago
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <style>{`.form-input { width: 100%; padding: 0.6rem 0.85rem; border: 1px solid var(--border); border-radius: 0.65rem; background: white; outline: none; font-size: 0.9rem; }
          .form-input:focus { border-color: var(--primary); }`}</style>
      </div>
    </div>
  );
}

function PaymentsTable({ payments, busy, onRemove }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useTableSort(payments, "date", "desc");
  const sh = { currentKey: sortKey, currentDir: sortDir, onClick: toggleSort };
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
          <th className="text-left p-2.5"><SortHeader sortKey="date" {...sh}>Fecha</SortHeader></th>
          <th className="text-right p-2.5"><SortHeader align="right" sortKey="amount" {...sh}>Importe</SortHeader></th>
          <th className="text-left p-2.5"><SortHeader sortKey="notes" {...sh}>Notas</SortHeader></th>
          <th className="text-right p-2.5 label-cap">Acciones</th>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((p) => (
          <tr key={p.id} className="border-b border-[var(--border)] last:border-0" data-testid={`payment-row-${p.id}`}>
            <td className="p-2.5 font-mono">{fmtDate(p.date)}</td>
            <td className="p-2.5 text-right font-mono font-semibold text-emerald-700">{fmtEUR(p.amount)}</td>
            <td className="p-2.5 text-[var(--text-secondary)] italic">{p.notes || "—"}</td>
            <td className="p-2.5 text-right">
              <button
                onClick={() => onRemove(p.id)}
                disabled={busy}
                data-testid={`payment-delete-${p.id}`}
                className="text-[var(--text-secondary)] hover:text-red-700 p-1"
                aria-label="Eliminar pago"
              >
                <Trash size={14} weight="duotone" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Stat({ label, value, testId, tone }) {
  return (
    <div className="bg-white rounded-xl border border-[var(--border)] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mb-1">{label}</div>
      <div className={`font-mono font-bold text-lg ${tone || ""}`} data-testid={testId}>{value}</div>
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div className={span ? `md:col-span-${span}` : ""}>
      <label className="label-cap block mb-1 text-[10px]">{label}</label>
      {children}
    </div>
  );
}
